import React, { useEffect, useState } from 'react';
import { authApi } from '../../../services/api';
import { AlertCircle, CheckCircle, Eye, EyeOff, Loader2, Save } from 'lucide-react';

const UserAdminManagement = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    let mounted = true;

    const loadProfile = async () => {
      try {
        setProfileLoading(true);
        const res = await authApi.getProfile();
        if (!mounted) return;
        if (res?.success && res.data) {
          setEmail(String(res.data.email || ''));
          try {
            const rawUser = localStorage.getItem('user');
            const parsedUser = rawUser ? JSON.parse(rawUser) : {};
            localStorage.setItem('user', JSON.stringify({ ...parsedUser, ...res.data }));
          } catch {
            void 0;
          }
        } else {
          setMessage({ type: 'error', text: 'Failed to load current account details.' });
        }
      } catch {
        if (mounted) {
          setMessage({ type: 'error', text: 'Failed to load current account details.' });
        }
      } finally {
        if (mounted) setProfileLoading(false);
      }
    };

    loadProfile();
    return () => {
      mounted = false;
    };
  }, []);

  const handleUpdate = async () => {
    setMessage(null);

    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Email is required.' });
      return;
    }

    if (password && password.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters.' });
      return;
    }

    if (password !== confirmPassword) {
      setMessage({ type: 'error', text: 'Password and confirm password do not match.' });
      return;
    }

    setLoading(true);
    try {
      const payload: { email?: string; password?: string } = { email: email.trim() };
      if (password) payload.password = password;

      const res = await authApi.updateProfile(payload);
      if (res?.success) {
        try {
          const rawUser = localStorage.getItem('user');
          const parsedUser = rawUser ? JSON.parse(rawUser) : {};
          localStorage.setItem('user', JSON.stringify({ ...parsedUser, email: email.trim() }));
        } catch {
          void 0;
        }
        setPassword('');
        setConfirmPassword('');
        setMessage({ type: 'success', text: 'Dashboard account updated successfully.' });
      } else {
        setMessage({ type: 'error', text: res?.message || 'Failed to update account.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to update account.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#0b0c15]/50 border border-white/10 rounded-2xl p-6">
      <h3 className="text-xl font-bold mb-2">Dashboard Account</h3>
      <p className="text-sm text-white/55 mb-6">Email aur password yahan se update kar sakte ho.</p>

      {message && (
        <div
          className={`mb-4 rounded-lg p-3 flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-500/15 text-green-300' : 'bg-red-500/15 text-red-300'
          }`}
        >
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          <span>{message.text}</span>
        </div>
      )}

      {profileLoading ? (
        <div className="h-[188px] flex items-center justify-center text-white/60">
          <Loader2 className="animate-spin mr-2" size={18} />
          Loading account...
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-cyan-500 transition-colors"
              placeholder="admin@example.com"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">New Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 pr-10 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                placeholder="Leave empty to keep current password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 pr-10 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                placeholder="Re-enter new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={handleUpdate}
            disabled={loading}
            className="w-full py-2 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Save size={18} />
                Update Account
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserAdminManagement;
